import React, { Component, Fragment } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';

import classNames from 'classnames';
import Tooltip from 'components/cosmos/tooltip';
import { ReactComponent as CalendarPlusIcon } from 'components/svg/icons/calendar-plus.svg';
import { ReactComponent as CollapseAllIcon } from 'components/svg/icons/collapse-all-lg.svg';
import { ReactComponent as DeleteIcon } from 'components/svg/icons/delete-lg.svg';
import { ReactComponent as ExpandAllIcon } from 'components/svg/icons/expand-all-lg.svg';
import { ReactComponent as PublishIcon } from 'components/svg/icons/publish.svg';
import { ReactComponent as UnpublishIcon } from 'components/svg/icons/unpublish.svg';
import { translate } from 'components/translate';
import { ClubWebSocketUtil } from 'helpers/clubWebSocket';
import { withShellContext } from 'hoc/withShellContext';
import difference from 'lodash/difference';
import flowRight from 'lodash/flowRight';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import { inject, PropTypes as MobxPropTypes } from 'mobx-react';
import { ComponentNames, DatahubEventsService } from 'modules/app/services';
import {
  EVENT_ACTIONS,
  EVENTS_CATEGORY,
  EVENTS_INTERACTIONS,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import ClickDeleteContentSchema from 'modules/app/services/datahubEvents/module/clickDeleteContentSchema';
import { ACTIONS, CATEGORIES, LABELS } from 'modules/app/services/ga/constants';
import GtagService from 'modules/app/services/ga/GtagService';
import {
  ContentPageService,
  ContentService,
  SeasonService
} from 'modules/content';
import { ITEM_TYPE } from 'modules/content/constants';
import BulkDrippingWizard from 'modules/content/dripping/components/bulkDrippingWizard';
import ModuleUtil from 'modules/content/moduleService/ModuleUtil';
import { FeatureToggles } from 'modules/featureToggle';
import MembershipService from 'modules/membership/MembershipService';
import { UiService } from 'modules/ui';
import { ALERT, ERROR_CODE } from 'modules/ui/constants';
import PropTypes from 'prop-types';

import BulkScheduleButton from './bulkScheduleButton';
import DroppableContent from './DroppableContent';
import FloatingBulkActionMenu from './floatingBulkActionMenu/floatingBulkActionMenu';

import ContentEvents from '../../events';
import CustomTooltip from '../../onboarding/components/CustomTooltip';
import OnboardingService from '../../onboarding/OnboardingService';
import ContentDeleteModal from '../ContentDeleteModal';
import DuplicationModal from '../duplication/DuplicationModal';
import { watchDuplicationStatus } from '../duplication/duplicationWebsocket';
import PublishAndNotifyDropdown from '../PublishAndNotifyDropdown';
import MassActionWizard from '../scheduler/MassActionWizard';
import SchedularWizard from '../scheduler/SchedulerWizard';

import './SortableContent.scss';

const handleReorderError = () => {
  UiService.setAlertError('unexpected_error');
};

const handleReorderSuccess = () => {
  UiService.setAlertSuccess('done');
};

const reorder = (type, list, startIndex, endIndex, moduleIndex = null) => {
  const result = JSON.parse(JSON.stringify(list));
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  if (type === ITEM_TYPE.MODULE) {
    ContentService.reOrderModule(removed.id, list[endIndex].id)
      .then(handleReorderSuccess)
      .catch(handleReorderError);
  }

  if (type === ITEM_TYPE.PAGE) {
    ContentService.reOrderPage(removed.id, list[endIndex].id, moduleIndex)
      .then(handleReorderSuccess)
      .catch(handleReorderError);
  }

  return result;
};

const hasElementsToReorder = (beginIndex, destinationIndex, targetIndex) => {
  return beginIndex > -1 && destinationIndex > -1 && targetIndex > -1;
};

const reorderDiffModule = (list, begin, destination, id) => {
  const result = JSON.parse(JSON.stringify(list));
  const beginIndex = result.findIndex(
    (element) => element.id === begin.droppableId
  );
  const destinationIndex = result.findIndex(
    (element) => element.id === destination.droppableId
  );
  const targetIndex = result[beginIndex].pages.findIndex(
    (element) => element.id === id
  );

  if (hasElementsToReorder(beginIndex, destinationIndex, targetIndex)) {
    const current = result[beginIndex].pages.concat();
    const next =
      result[destinationIndex].pages && result[destinationIndex].pages.concat();
    const target = result[beginIndex].pages[targetIndex];
    let beforeElement = null;

    current.splice(begin.index, 1);
    result[beginIndex].pages = current;
    result[beginIndex].totalPages = current.length;

    if (next) {
      if (next.length && destination.index < next.length) {
        beforeElement = list[destinationIndex].pages[destination.index].id;
        next.splice(destination.index, 0, target);
      } else {
        next.push(target);
      }

      result[destinationIndex].pages = next;
    }

    result[destinationIndex].totalPages += 1;
    ContentService.reOrderPage(
      target.id,
      beforeElement,
      destination.droppableId
    );
  }

  return result;
};

const itemsHaveChanged = (items1, items2) => {
  return JSON.stringify(items1) !== JSON.stringify(items2);
};

class SortableContent extends Component {
  static propTypes = {
    id: PropTypes.string,
    isExtra: PropTypes.bool,
    isMobile: PropTypes.bool,
    items: MobxPropTypes.arrayOrObservableArray,
    type: PropTypes.string,
    location: PropTypes.object,
    UiStore: MobxPropTypes.objectOrObservableObject,
    onboarding: MobxPropTypes.objectOrObservableObject,
    openPublishAndNotifyModal: PropTypes.func,
    showNotifyModal: PropTypes.bool,
    emailNotificationBeta: PropTypes.bool,
    t: PropTypes.func
  };

  state = {
    items: this.props.items,
    expandModules: false,
    isPageDragging: false,
    isModuleDragging: false,
    isDuplicating: false,
    checkedContent: {
      modules: [],
      pages: [],
      nPublishedModule: 0,
      nPublished: 0,
      nUnpublished: 0,
      nDripping: 0
    },
    showModalMass: false,
    showDuplicationModal: false,
    duplicatedModule: {},
    isAllChecked: false,
    isAllIndeterminate: false,
    nCheckedPagesWithDripping: 0
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!nextProps || !prevState) {
      return null;
    }

    if (itemsHaveChanged(nextProps.items, prevState.items)) {
      return { items: nextProps.items };
    }

    return null;
  }

  componentDidUpdate(prevProps) {
    const { items } = this.props;

    if (itemsHaveChanged(prevProps.items, items)) {
      this.clearCheckeds();
    }
  }

  onMouseDown = (e, beforeCallback, callback) => {
    e.persist();
    e.stopPropagation();
    const triggerElClasses = e.currentTarget.className;

    if (triggerElClasses.includes('module')) {
      beforeCallback && beforeCallback();
    }

    this.setState(
      {
        isModuleDragging: triggerElClasses.includes('module'),
        isPageDragging: triggerElClasses.includes('page')
      },
      () => callback && callback(e)
    );
  };

  onMouseUp = (e, afterCallback) => {
    e.stopPropagation();
    const triggerElClasses = e.target.className;

    if (typeof triggerElClasses !== 'string') {
      return;
    }

    if (triggerElClasses.includes('module')) {
      afterCallback?.();
      this.setState({ isModuleDragging: false });
    } else if (triggerElClasses.includes('page')) {
      this.setState({ isPageDragging: false });
    }
  };

  checkIfPageCanBeDropped = (destination, target) => {
    const { isExtra } = this.props;

    return isExtra
      ? true
      : MembershipService.isStreamingFreeContentEnabled ||
          !(destination.publicModule && target.hasMedia);
  };

  canBeDropped = (result) => {
    if (!result.source || !result.destination) {
      return true;
    }

    if (result.type === ITEM_TYPE.PAGE) {
      const items = [...this.state.items];
      const beginIndex = items.findIndex(
        (element) => element.id === result.source.droppableId
      );
      const destinationIndex = items.findIndex(
        (element) => element.id === result.destination.droppableId
      );
      const targetIndex = items[beginIndex].pages.findIndex(
        (element) => element.id === result.draggableId
      );

      if (beginIndex > -1 && destinationIndex > -1 && targetIndex > -1) {
        const next = items[destinationIndex];
        const target = items[beginIndex].pages[targetIndex];

        return this.checkIfPageCanBeDropped(next, target);
      }
    }

    return true;
  };
  onDragEndModule = (result, items) => {
    const { isDuplicating } = this.state;

    if (isDuplicating) {
      return items;
    }

    const destination = result.destination.index;
    const begin = result.source.index;

    items = reorder(result.type, items, begin, destination);

    this.setState({ isModuleDragging: false });

    return items;
  };

  onDragEndPage = (result, items) => {
    const destination = result.destination;
    const begin = result.source;

    if (destination.droppableId === begin.droppableId) {
      const index = items.findIndex(
        (element) => element.id === destination.droppableId
      );

      if (index > -1) {
        items[index].pages = reorder(
          result.type,
          items[index].pages,
          begin.index,
          destination.index,
          items[index].id
        );
      }
    } else {
      items = reorderDiffModule(items, begin, destination, result.draggableId);
    }

    this.setState({ isPageDragging: false });

    return items;
  };

  onDragEnd = (result) => {
    const { isExtra } = this.props;
    let items = [...this.state.items];
    const canBeDropped = this.canBeDropped(result);

    if (
      !result.destination ||
      !result.destination.droppableId ||
      !canBeDropped
    ) {
      this.setState({ isModuleDragging: false });
      this.setState({ isPageDragging: false });

      if (!canBeDropped) {
        UiService.setAlertError(
          'cannot_move_page_with_streaming_to_free_module'
        );
      }

      return;
    }
    items =
      result.type === ITEM_TYPE.MODULE
        ? this.onDragEndModule(result, items)
        : this.onDragEndPage(result, items);

    isExtra
      ? ContentService.setExtraData(items)
      : ContentService.setPrincipalData(items);
  };

  toggleModules = () => {
    ContentService.lastEditedModule = null;

    ContentEvents.sendClick({ label: 'ExpandModules' });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CONTENT.NAME,
        action: EVENTS_INTERACTIONS.CLICK,
        label: EVENTS_LABELS.CONTENT.EXPAND_MODULES
      })
    });

    this.setState({ expandModules: !this.state.expandModules });
  };

  propsToArray(array, prop) {
    return array.map((item) => {
      if (item[prop]) {
        return item[prop];
      } else {
        return item;
      }
    });
  }

  uncheckPages(item, checkedContent) {
    return difference(checkedContent, this.propsToArray(item.pages, 'id'));
  }

  checkPages(item, checkedContent) {
    return union(checkedContent, this.propsToArray(item.pages, 'id'));
  }

  countPagesDifference(item, checkedPages, published) {
    const pages = item.pages.filter(
      (obj) => obj && obj.published === published
    );

    return difference(this.propsToArray(pages, 'id'), checkedPages).length;
  }

  countPagesIntersection(item, checkedPages, published) {
    const pages = item.pages.filter(
      (obj) => obj && obj.published === published
    );

    return intersection(this.propsToArray(pages, 'id'), checkedPages).length;
  }

  isPageDripping(page) {
    return page.liberationDate || page.liberationDays;
  }

  getDrippingPages(pages = []) {
    return pages.filter((page) => page && this.isPageDripping(page));
  }

  countDrippingDifference(item, checkedPages) {
    const pages = this.getDrippingPages(item.pages);

    return difference(this.propsToArray(pages, 'id'), checkedPages).length;
  }

  countDrippingIntersection(item, checkedPages) {
    const pages = this.getDrippingPages(item.pages);

    return intersection(this.propsToArray(pages, 'id'), checkedPages).length;
  }

  updateCheckedPages(item, index, checkedContentList, allChecked) {
    if (item.pages) {
      let checkedPages = checkedContentList.pages;

      if (index > -1 || allChecked) {
        checkedContentList.nPublished -= this.countPagesIntersection(
          item,
          checkedPages,
          true
        );
        checkedContentList.nUnpublished -= this.countPagesIntersection(
          item,
          checkedPages,
          false
        );
        checkedContentList.nDripping -= this.countDrippingIntersection(
          item,
          checkedPages
        );

        checkedPages = this.uncheckPages(item, checkedPages);
      } else {
        checkedContentList.nPublished += this.countPagesDifference(
          item,
          checkedPages,
          true
        );
        checkedContentList.nUnpublished += this.countPagesDifference(
          item,
          checkedPages,
          false
        );
        checkedContentList.nDripping += this.countDrippingDifference(
          item,
          checkedPages
        );

        checkedPages = this.checkPages(item, checkedPages);
      }

      checkedContentList.pages = checkedPages;
    }

    return checkedContentList;
  }

  moduleCheckboxChange(checkedContentList, moduleId) {
    if (checkedContentList && checkedContentList.modules) {
      const { items } = this.props;

      const checkedModules = checkedContentList.modules;
      const indexChecked = checkedModules.indexOf(moduleId);
      const item = items.find((element) => element.id === moduleId);
      let allPagesChecked = false;

      if (indexChecked > -1) {
        checkedModules.splice(indexChecked, 1);
      } else {
        checkedModules.push(moduleId);

        if (item.pages) {
          const checkedPagesModule = intersection(
            checkedContentList.pages,
            this.propsToArray(item.pages, 'id')
          );

          allPagesChecked =
            checkedPagesModule.length > 0 &&
            checkedPagesModule.length === item.pages.length;
        }
      }

      checkedContentList = this.updateCheckedPages(
        item,
        indexChecked,
        checkedContentList,
        allPagesChecked
      );
      checkedContentList.modules = checkedModules;
      checkedContentList.nPublishedModule += 1;

      return checkedContentList;
    }
  }

  checkAllpages = (checkedContent, pages) => {
    pages.forEach((element) => {
      if (element) {
        checkedContent.pages.push(element.id);

        if (element.published) {
          checkedContent.nPublished++;
        } else {
          checkedContent.nUnpublished++;
        }
      }
    });

    this.setState({ checkedContent });
  };

  shouldDisableNotify = () => {
    const { checkedContent } = this.state;
    const allDripping = this.areAllDripping(checkedContent);

    return allDripping;
  };

  // eslint-disable-next-line complexity
  pageCheckboxChange = (checkedContentList, pageId, published, page) => {
    if (checkedContentList && checkedContentList.pages) {
      const { items } = this.props;

      const checkedPages = checkedContentList.pages;
      const indexChecked = checkedPages.indexOf(pageId);
      const isUnchecking = indexChecked > -1;

      if (isUnchecking) {
        checkedPages.splice(indexChecked, 1);
        if (published) {
          checkedContentList.nPublished--;
        } else {
          checkedContentList.nUnpublished--;
        }

        if (this.isPageDripping(page)) {
          checkedContentList.nDripping--;
        }
      } else {
        checkedPages.push(pageId);
        if (published) {
          checkedContentList.nPublished++;
        } else {
          checkedContentList.nUnpublished++;
        }

        if (this.isPageDripping(page)) {
          checkedContentList.nDripping++;
        }
      }

      checkedContentList.pages = checkedPages;

      const module = (items || []).find((item) =>
        (item?.pages || []).find((page) => page.id === pageId)
      );

      if ((module?.pages || []).length !== (checkedPages || []).length) {
        const indexModule = items.findIndex((item) => item.id === module.id);

        checkedContentList.modules.splice(indexModule, 1);
      } else {
        checkedContentList.modules.push(module.id);
      }

      return checkedContentList;
    }
  };

  allCheckboxChange = (checkedContentList, isAllChecked) => {
    const { items } = this.state;
    let allContent = { ...checkedContentList };

    items.forEach((item) => {
      const areAllSelected = this.areAllPagesSelected(allContent, item);

      if (
        (isAllChecked && !areAllSelected) ||
        (!isAllChecked && areAllSelected)
      ) {
        allContent = this.moduleCheckboxChange(allContent, item.id);
      }
    });

    return allContent;
  };

  checkboxChange = (e, item) => {
    const { checkedContent } = this.state;
    const { isAllChecked, isAllIndeterminate } = this.state;

    const target = e.target;
    const moduleId = target.getAttribute('data-module');
    const pageId = target.getAttribute('data-page');
    const allCheckbox = target.getAttribute('data-all');
    let checkedContentList = Object.assign({}, checkedContent);
    let allChecked = isAllChecked;
    let allIndeterminate = isAllIndeterminate;

    if (moduleId) {
      checkedContentList = this.moduleCheckboxChange(
        checkedContentList,
        moduleId
      );
    }

    if (pageId) {
      const published = target.getAttribute('data-published') === 'true';

      checkedContentList = this.pageCheckboxChange(
        checkedContentList,
        pageId,
        published,
        item
      );
    }

    if (allCheckbox) {
      allChecked = !allChecked;
      checkedContentList = this.allCheckboxChange(
        checkedContentList,
        allChecked
      );
    } else {
      allChecked = this.areAllModulesSelected(checkedContentList);
    }

    allIndeterminate = Boolean(
      checkedContentList.modules.length || checkedContentList.pages.length
    );

    this.setState({
      checkedContent: checkedContentList,
      isAllChecked: allChecked,
      isAllIndeterminate: allIndeterminate
    });
  };

  areAllModulesSelected = (checkedContentList) => {
    const { items } = this.state;

    const isAllPagesChecked = items.every((item) =>
      this.areAllPagesSelected(checkedContentList, item)
    );

    return isAllPagesChecked;
  };

  areAllPagesSelected = (checkedContentList, item) => {
    const moduleChecked = checkedContentList.modules.includes(item.id);
    const allPagesChecked =
      item.pages &&
      item.pages.every((page) => checkedContentList.pages.includes(page.id));

    return moduleChecked || allPagesChecked;
  };

  updateModulesList = () => {
    const { isExtra } = this.props;
    const seasonId = MembershipService.hasSeason
      ? SeasonService.getSelectedSeasonInfo().id
      : null;

    return ContentService.fetchModulesList(seasonId, isExtra);
  };

  massPublish = async () => {
    const { checkedContent } = this.state;

    await ContentPageService.publish(
      checkedContent.pages,
      true,
      checkedContent.modules
    );
    await this.updateModulesList();
    await OnboardingService.updateStatusContentAndApprovedStatus();
    ContentEvents.sendClick({ label: 'Publish' });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CONTENT.NAME,
        action: EVENTS_INTERACTIONS.CLICK,
        label: EVENTS_LABELS.CONTENT.PUBLISH
      })
    });

    this.clearCheckeds();
    this.setState({ showModalMass: false });
  };

  massPublishAndNotify = async () => {
    const { checkedContent } = this.state;
    const status = true;
    const showAlert = false;
    const notifyByEmail = true;
    const response = await ContentPageService.publish(
      checkedContent.pages,
      status,
      checkedContent.modules,
      showAlert,
      notifyByEmail
    );

    if (response) {
      if (response.alreayNotifiedByEmail) {
        UiService.setAlertInfo(
          'publish_and_notify_error',
          {},
          ALERT.TIMEOUT.LONG
        );
      } else {
        GtagService.sendClick({
          category: CATEGORIES.CLUB_ADMIN_CONTENT,
          where: ACTIONS.PUBLISH_AND_NOTIFY,
          label: LABELS.PUBLISH_AND_NOTIFY
        });

        DatahubService.sendEvent({
          action: EVENT_ACTIONS.INTERACT,
          event: buildInteractionEventData({
            category: EVENTS_CATEGORY.CONTENT.NAME,
            action: EVENTS_INTERACTIONS.CLICK,
            label: EVENTS_LABELS.CONTENT.PUBLISH_AND_NOTIFY
          })
        });

        UiService.setAlertSuccess(
          'publish_and_notify_success',
          {},
          ALERT.TIMEOUT.LONG
        );
      }
    }

    await this.updateModulesList();
    await OnboardingService.updateStatusContentAndApprovedStatus();
    ContentEvents.sendClick({ label: 'Publish' });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CONTENT.NAME,
        action: EVENTS_INTERACTIONS.CLICK,
        label: EVENTS_LABELS.CONTENT.PUBLISH
      })
    });

    this.clearCheckeds();
    this.setState({ showModalMass: false });
  };

  massUnpublish = async () => {
    const { checkedContent } = this.state;

    await ContentPageService.publish(
      checkedContent.pages,
      false,
      checkedContent.modules
    );
    await this.updateModulesList();
    await OnboardingService.updateStatusContentAndApprovedStatus();

    ContentEvents.sendClick({ label: 'UndoPublish' });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CONTENT.NAME,
        action: EVENTS_INTERACTIONS.CLICK,
        label: EVENTS_LABELS.CONTENT.UNDO_PUBLISH
      })
    });

    this.clearCheckeds();
    this.setState({ showModalMass: false });
  };

  updateCheckedModule = (moduleId) => {
    const checkedContent = {
      modules: [moduleId],
      pages: []
    };

    this.setState({ checkedContent });
  };

  massDelete = async () => {
    const { checkedContent } = this.state;
    let errorCode = null;
    let success = true;

    try {
      await ContentService.massDelete(
        checkedContent.modules,
        checkedContent.pages
      );
      UiService.setAlertSuccess('content_removed_success');
    } catch (error) {
      const hasBlockingQuiz =
        error?.data?.errorCode === ERROR_CODE.BLOCKING_QUIZ;

      // Change error code because the API does not distinguish between single and bulk deletes
      if (hasBlockingQuiz) {
        error.data.errorCode = ERROR_CODE.BLOCKING_QUIZ_BULK;
      }

      UiService.handleError(error);
      errorCode = error?.data?.errorCode || null;
      success = false;
      return;
    } finally {
      DatahubEventsService.sendEvent({
        schema: new ClickDeleteContentSchema({
          success,
          errorCode,
          componentName: ComponentNames.BUTTON_DELETE_CONTENT
        })
      });
    }

    this.updateModulesList();
    OnboardingService.updateStatusContentAndApprovedStatus();
    ContentEvents.sendClick({ label: 'Remove' });
    this.clearCheckeds();
    this.setState({ showModalMass: false });
  };

  onMassScheduling = async () => {
    await this.updateModulesList();
    await OnboardingService.updateStatusContentAndApprovedStatus();

    this.clearCheckeds();
  };

  onBulkDrippingFinish = async () => {
    this.clearCheckeds();
    await this.updateModulesList();
  };

  closeModalMass = () => {
    this.setState({ showModalMass: false });
  };

  showModalMass = () => {
    this.setState({ showModalMass: true });
  };

  openDuplicationModal = (id, name, classes) => {
    const { t } = this.props;
    const newName = `${t('general.copy_of')} ${name}`;

    this.setState({
      showDuplicationModal: true,
      duplicatedModule: { id, name: newName, classes }
    });
  };

  closeDuplicationModal = () => {
    this.setState({ showDuplicationModal: false });
  };

  clearCheckeds = () => {
    this.setState({
      checkedContent: {
        modules: [],
        pages: [],
        nPublished: 0,
        nUnpublished: 0
      },
      isAllChecked: false,
      isAllIndeterminate: false
    });
  };

  hasCheckedModules(checkedContent) {
    return checkedContent.modules && checkedContent.modules.length > 0;
  }

  hasCheckedPages(checkedContent) {
    return checkedContent.pages && checkedContent.pages.length > 0;
  }

  hasCheckedItems(checkedContent) {
    return (
      this.hasCheckedModules(checkedContent) ||
      this.hasCheckedPages(checkedContent)
    );
  }

  areAllPublished(checkedContent) {
    return (
      !this.hasCheckedModules(checkedContent) &&
      checkedContent.pages.length === checkedContent.nPublished
    );
  }

  areAllUnpublished(checkedContent) {
    return (
      !this.hasCheckedModules(checkedContent) &&
      checkedContent.pages.length === checkedContent.nUnpublished
    );
  }

  areAllDripping(checkedContent) {
    return (
      !this.hasCheckedModules(checkedContent) &&
      checkedContent.pages.length === checkedContent.nDripping
    );
  }

  pulseAnimationAtPublishMass() {
    const { onboarding = {} } = this.props;

    try {
      return (
        onboarding.showVersionWithOnboarding &&
        onboarding.checkList.hasModule &&
        onboarding.checkList.hasContent &&
        !onboarding.checkList.publishedContent
      );
    } catch {
      return false;
    }
  }

  getMassPublishPulsingClassName() {
    return this.pulseAnimationAtPublishMass()
      ? 'content-list__mass-publish--pulsed'
      : '';
  }

  wrapperButtonPublishMass() {
    return this.pulseAnimationAtPublishMass()
      ? (innerComponent) => (
          <CustomTooltip
            text="onboarding.online_event.select_and_publish"
            position="top"
          >
            {innerComponent}
          </CustomTooltip>
        )
      : null;
  }

  onReceiveDuplicationStatus = async () => {
    await this.updateModulesList();
    this.setState({ isDuplicating: false });
  };

  handleFloatingBulkActionMenuClose = (updateList) => {
    this.clearCheckeds();

    if (updateList) {
      this.updateModulesList();
    }
  };

  duplicate = async (params) => {
    this.closeDuplicationModal();
    this.setState({ isDuplicating: true });

    try {
      const contextId = ClubWebSocketUtil.generateContextId();
      const response = await ContentService.duplicate({ ...params, contextId });

      if (response === false) {
        UiService.setAlertError('duplication_error', {});
        this.setState({ isDuplicating: false });

        return;
      }

      watchDuplicationStatus({
        contextId,
        onReceive: this.onReceiveDuplicationStatus
      });
    } catch (e) {
      UiService.setAlertError('duplication_error', {});
      this.setState({ isDuplicating: false });
    }
  };

  renderAccordion = () => {
    const { t, isMobile } = this.props;
    const { expandModules } = this.state;
    const AccordionIcon = expandModules ? CollapseAllIcon : ExpandAllIcon;
    const content = expandModules
      ? t('content_list.close_modules')
      : t('content_list.expand_modules');
    const Button = (
      <button
        className="hot-button hot-button--tertiary"
        onClick={this.toggleModules}
      >
        <AccordionIcon className="accordion-icon" />
      </button>
    );

    return (
      <span className="content-list__options__accordion">
        {isMobile ? (
          Button
        ) : (
          <Tooltip content={content} position={Tooltip.TOOLTIP_POSITION.TOP}>
            {Button}
          </Tooltip>
        )}
      </span>
    );
  };

  // eslint-disable-next-line complexity
  renderMassActions = () => {
    const {
      isMobile,
      openPublishAndNotifyModal,
      showNotifyModal,
      emailNotificationBeta,
      isDrippingByClassEnabled,
      bulkActionMenuToggle,
      t
    } = this.props;
    const { checkedContent, isAllChecked } = this.state;
    const hasCheckedItems = this.hasCheckedItems(checkedContent);
    const allPublished = this.areAllPublished(checkedContent);
    const allUnpublished = this.areAllUnpublished(checkedContent);
    const disablePublish = allPublished || !hasCheckedItems;
    const disableUnpublish = allUnpublished || !hasCheckedItems;

    const renderAction = (action) => {
      const { wrapper } = action;
      const Icon = action.icon;
      const button = (
        <button
          className={classNames(
            'hot-button',
            'hot-button--tertiary',
            action.className ? action.className : ''
          )}
          disabled={action.disabled}
          onClick={action.onClick}
        >
          <Icon
            className={classNames('icon', {
              '-disabled': action.disabled
            })}
          />
          {action.label}
        </button>
      );

      return wrapper ? wrapper(button) : button;
    };

    const actions = {
      publish: {
        wrapper: this.wrapperButtonPublishMass(),
        label: t('general.publish'),
        icon: PublishIcon,
        onClick: this.massPublish,
        disabled: disablePublish,
        className: this.getMassPublishPulsingClassName()
      },
      unpublish: {
        label: t('general.unpublish'),
        icon: UnpublishIcon,
        onClick: this.massUnpublish,
        disabled: disableUnpublish
      },
      schedule: {
        label: t('general.schedule'),
        icon: CalendarPlusIcon,
        onClick: this.openScheduler,
        disabled: !hasCheckedItems,
        wrapper: (button) => (
          <SchedularWizard
            checkedContent={checkedContent}
            button={button}
            onSuccess={this.onMassScheduling}
          />
        )
      },
      exclude: {
        label: t('general.exclude'),
        icon: DeleteIcon,
        onClick: this.showModalMass,
        disabled: !hasCheckedItems
      }
    };

    if (!isMobile) {
      return (
        <>
          <div className="hot-form hot-form--custom hot-form--checkbox _mb-0">
            <Tooltip
              content={t('content_list.select_all_description')}
              position={Tooltip.TOOLTIP_POSITION.RIGHT}
            >
              <input
                type="checkbox"
                className="hot-form__input _mb-0"
                id="checkbox-select-all"
                checked={isAllChecked}
                onChange={this.checkboxChange}
                data-all={true}
                data-test="ca-checkbox-select-all"
              />
              <label className="hot-form__label" htmlFor="checkbox-select-all">
                {t('general.select_all_alt')}
              </label>
            </Tooltip>
          </div>
          <div className="content-list__options__buttons">
            {!bulkActionMenuToggle && (
              <>
                {emailNotificationBeta ? (
                  <PublishAndNotifyDropdown
                    openModal={openPublishAndNotifyModal}
                    onPublish={this.massPublish}
                    onPublishAndNotify={this.massPublishAndNotify}
                    shouldShowModal={showNotifyModal}
                    disable={disablePublish}
                    disableNotify={this.shouldDisableNotify()}
                  />
                ) : (
                  renderAction(actions.publish)
                )}
                {renderAction(actions.unpublish)}
                {isDrippingByClassEnabled ? (
                  <BulkScheduleButton isDisabled={actions.schedule.disabled} />
                ) : (
                  renderAction(actions.schedule)
                )}
                {renderAction(actions.exclude)}
              </>
            )}

            {this.renderAccordion()}
          </div>
        </>
      );
    }

    const getPublishAndNotify = () => {
      if (!emailNotificationBeta) {
        return null;
      }

      if (showNotifyModal) {
        return () => openPublishAndNotifyModal(this.massPublishAndNotify);
      }

      return this.massPublishAndNotify;
    };

    const button = (
      <button
        slot="button"
        className={classNames(
          'hot-button',
          'hot-dropdown__toggle',
          this.getMassPublishPulsingClassName()
        )}
        disabled={!hasCheckedItems}
      >
        {t('content_list.mass_actions')}
      </button>
    );
    const wrapper = (button) => (
      <MassActionWizard
        checkedContent={checkedContent}
        exclude={this.showModalMass}
        unpublish={this.massUnpublish}
        publish={this.massPublish}
        onSuccess={this.onMassScheduling}
        publishAndNotify={getPublishAndNotify()}
        disablePublish={disablePublish}
        disableUnpublish={disableUnpublish}
        button={button}
      />
    );

    return (
      <>
        <div className="hot-form hot-form--custom hot-form--checkbox _mb-0">
          <input
            type="checkbox"
            className="hot-form__input"
            id="checkbox-select-all"
            checked={isAllChecked}
            onChange={this.checkboxChange}
            data-all={true}
            data-test="ca-checkbox-select-all"
          />
          <label className="hot-form__label" htmlFor="checkbox-select-all">
            {bulkActionMenuToggle && t('general.select_all_alt')}
          </label>
        </div>
        {!bulkActionMenuToggle && wrapper(button)}
        {this.renderAccordion()}
      </>
    );
  };

  renderOptions = () => {
    return (
      <div className="content-list__options">{this.renderMassActions()}</div>
    );
  };

  // eslint-disable-next-line complexity
  render() {
    const {
      id,
      type,
      isExtra,
      isMobile,
      isDrippingByClassEnabled,
      moduleList
    } = this.props;
    const {
      items,
      expandModules,
      isModuleDragging,
      isPageDragging,
      checkedContent,
      showModalMass,
      showDuplicationModal,
      duplicatedModule
    } = this.state;
    const componentClassName = classNames('droppable-module', {
      'droppable-module-dragging': isModuleDragging
    });

    return (
      <Fragment>
        <FloatingBulkActionMenu
          checkedContent={checkedContent}
          isExtra={isExtra}
          onClose={this.handleFloatingBulkActionMenuClose}
        />
        {showModalMass && (
          <ContentDeleteModal
            numModules={checkedContent.modules.length}
            numPages={checkedContent.pages.length}
            allModules={this.areAllModulesSelected(checkedContent)}
            onCancel={this.closeModalMass}
            onConfirm={this.massDelete}
          />
        )}

        {isMobile && (
          <div className="content-list__options__background">
            {this.renderOptions()}
          </div>
        )}

        {this.renderOptions()}

        <div className="droppable-module-container">
          <DragDropContext
            onDragEnd={this.onDragEnd}
            onBeforeDragStart={this.onBeforeDragStart}
          >
            <DroppableContent
              checkboxChange={this.checkboxChange}
              checkedContent={checkedContent}
              checkAllpages={this.checkAllpages}
              clearCheckeds={this.clearCheckeds}
              expandModules={expandModules}
              id={id}
              className={componentClassName}
              isExtra={isExtra ? isExtra : false}
              isMobile={isMobile}
              isModuleDragging={isModuleDragging}
              isPageDragging={isPageDragging}
              onMouseDown={this.onMouseDown}
              onMouseUp={this.onMouseUp}
              openDuplicationModal={this.openDuplicationModal}
              showModalMass={this.showModalMass}
              updateCheckedModule={this.updateCheckedModule}
              type={type}
              items={items}
            />
          </DragDropContext>
        </div>

        {showDuplicationModal && (
          <DuplicationModal
            isOpen={showDuplicationModal}
            onCancel={this.closeDuplicationModal}
            module={duplicatedModule}
            onConfirm={this.duplicate}
          />
        )}
        {isDrippingByClassEnabled && (
          <BulkDrippingWizard
            modules={checkedContent.modules || []}
            pages={checkedContent.pages || []}
            total={ModuleUtil.getTotalSelectedItems(
              checkedContent.modules,
              checkedContent.pages,
              moduleList || []
            )}
            onProcessFinish={this.onBulkDrippingFinish}
          />
        )}
      </Fragment>
    );
  }
}

export default flowRight(
  translate(),
  withShellContext,
  inject((stores, props) => ({
    isDrippingByClassEnabled: stores.FeatureToggleStore.featureToggles.get(
      FeatureToggles.DrippingByClass
    ),
    bulkActionMenuToggle:
      props.isMembership &&
      stores.FeatureToggleStore.featureToggles.get(
        FeatureToggles.BulkActionMenu
      ),
    moduleList: props.isExtra
      ? stores.ContentStore.modulesExtraList
      : stores.ContentStore.modulesList
  }))
)(SortableContent);
